import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Pill, CalendarPlus, Activity } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickActions = ({ selectedPatient, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [symptomForm, setSymptomForm] = useState({
    date: new Date().toISOString().split('T')[0],
    symptoms: [],
    customSymptom: '',
  });
  const [medicationForm, setMedicationForm] = useState({
    name: '',
    frequency: 'daily',
    dosage: '',
    reminder_times: ['09:00'],
  });

  const defaultSymptoms = ['Fatigue', 'Nausea', 'Pain', 'Loss of appetite', 'Sleep issues', 'Headache', 'Dizziness'];

  const handleAddSymptom = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API}/symptoms`,
        {
          patient_id: selectedPatient.id,
          date: symptomForm.date,
          symptoms: symptomForm.symptoms,
          notes: '',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Symptom added');
      setShowModal(null);
      setSymptomForm({ date: new Date().toISOString().split('T')[0], symptoms: [], customSymptom: '' });
      onUpdate();
    } catch (error) {
      toast.error('Failed to add symptom');
    }
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API}/medications`,
        { ...medicationForm, patient_id: selectedPatient.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Medication added');
      setShowModal(null);
      setMedicationForm({
        name: '',
        frequency: 'daily',
        dosage: '',
        reminder_times: ['09:00'],
      });
      onUpdate();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const toggleSymptom = (symptom) => {
    if (symptomForm.symptoms.includes(symptom)) {
      setSymptomForm({
        ...symptomForm,
        symptoms: symptomForm.symptoms.filter((s) => s !== symptom),
      });
    } else {
      setSymptomForm({
        ...symptomForm,
        symptoms: [...symptomForm.symptoms, symptom],
      });
    }
  };

  const addCustomSymptom = () => {
    if (symptomForm.customSymptom.trim()) {
      setSymptomForm({
        ...symptomForm,
        symptoms: [...symptomForm.symptoms, symptomForm.customSymptom.trim()],
        customSymptom: '',
      });
    }
  };

  const actions = [
    { id: 'symptom', label: 'Add Symptom', icon: Activity, color: '#A2DAD3' },
    { id: 'medication', label: 'Add Medication', icon: Pill, color: '#F9A7A7' },
  ];

  return (
    <>
      <div className="fixed bottom-[150px] md:bottom-24 left-6 z-[100]" data-testid="quick-actions">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className="mb-4 space-y-3"
            >
              {actions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <motion.button
                    key={action.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setShowModal(action.id)}
                    data-testid={`quick-action-${action.id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-full shadow-lg"
                    style={{ backgroundColor: action.color, color: 'white' }}
                  >
                    <Icon size={20} />
                    <span className="text-sm font-medium whitespace-nowrap">{action.label}</span>
                  </motion.button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          data-testid="fab-button"
          onClick={() => setIsOpen(!isOpen)}
          className="fab"
        >
          {isOpen ? <X size={24} /> : <Plus size={24} />}
        </button>
      </div>

      {/* Add Symptom Modal */}
      <Dialog open={showModal === 'symptom'} onOpenChange={() => setShowModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Symptom</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSymptom} className="space-y-4" data-testid="symptom-form">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                data-testid="symptom-date-input"
                type="date"
                value={symptomForm.date}
                onChange={(e) => setSymptomForm({ ...symptomForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Select Symptoms</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {defaultSymptoms.map((symptom) => (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => toggleSymptom(symptom)}
                    className="p-2 rounded-lg text-sm transition"
                    style={{
                      backgroundColor: symptomForm.symptoms.includes(symptom) ? '#A2DAD3' : '#f0f9f8',
                      color: symptomForm.symptoms.includes(symptom) ? 'white' : '#333',
                      border: `2px solid ${symptomForm.symptoms.includes(symptom) ? '#A2DAD3' : '#e5e5e5'}`,
                    }}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Other Symptoms</label>
              <div className="flex gap-2">
                <input
                  data-testid="custom-symptom-input"
                  type="text"
                  value={symptomForm.customSymptom}
                  onChange={(e) => setSymptomForm({ ...symptomForm, customSymptom: e.target.value })}
                  placeholder="Enter custom symptom"
                />
                <button
                  type="button"
                  onClick={addCustomSymptom}
                  className="btn btn-primary px-3"
                >
                  Add
                </button>
              </div>
              {symptomForm.symptoms.filter((s) => !defaultSymptoms.includes(s)).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {symptomForm.symptoms
                    .filter((s) => !defaultSymptoms.includes(s))
                    .map((symptom, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-sm"
                        style={{ backgroundColor: '#A2DAD3', color: 'white' }}
                      >
                        {symptom}
                      </span>
                    ))}
                </div>
              )}
            </div>
            <button
              data-testid="submit-symptom-button"
              type="submit"
              className="btn btn-primary w-full"
              disabled={symptomForm.symptoms.length === 0}
            >
              Add Symptom
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Medication Modal */}
      <Dialog open={showModal === 'medication'} onOpenChange={() => setShowModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Medication</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMedication} className="space-y-4" data-testid="medication-form">
            <div>
              <label className="block text-sm font-medium mb-2">Medication Name</label>
              <input
                data-testid="medication-name-input"
                type="text"
                value={medicationForm.name}
                onChange={(e) => setMedicationForm({ ...medicationForm, name: e.target.value })}
                required
                placeholder="e.g., Aspirin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Dosage</label>
              <input
                data-testid="medication-dosage-input"
                type="text"
                value={medicationForm.dosage}
                onChange={(e) => setMedicationForm({ ...medicationForm, dosage: e.target.value })}
                required
                placeholder="e.g., 500mg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Frequency</label>
              <select
                data-testid="medication-frequency-select"
                value={medicationForm.frequency}
                onChange={(e) => setMedicationForm({ ...medicationForm, frequency: e.target.value })}
              >
                <option value="daily">Daily</option>
                <option value="twice-daily">Twice a day</option>
                <option value="three-times-daily">Three times a day</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Reminder Time</label>
              <input
                data-testid="medication-time-input"
                type="time"
                value={medicationForm.reminder_times[0]}
                onChange={(e) =>
                  setMedicationForm({ ...medicationForm, reminder_times: [e.target.value] })
                }
                required
              />
            </div>
            <button data-testid="submit-medication-button" type="submit" className="btn btn-primary w-full">
              Add Medication
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickActions;
